<script setup>
// Бабл з новим повідомленням. Окремим компонентом, бо показуємо їх у двох
// місцях: праворуч знизу на сторінці і праворуч зверху у фулскріні плеєра.
//
// Клік відкриває той чат, звідки повідомлення. Прибрати бабл, нікуди не
// переходячи, можна хрестиком або свайпом убік.

defineProps({
  bubble: { type: Object, required: true }
})

const emit = defineEmits(['open', 'dismiss'])

// Далі якого зсуву відпускання рахується свайпом, а не промахом
const SWIPE_THRESHOLD = 60
// З якого зсуву вважаємо, що це вже жест, а не тремтіння пальця
const DRAG_START = 8
const FLY_OUT_MS = 160

const dx = ref(0)
const dragging = ref(false)
const flying = ref(false)

let startX = 0
let startY = 0
let decided = false
let moved = false

const onPointerDown = (e) => {
  if (flying.value || e.button > 0) return
  startX = e.clientX
  startY = e.clientY
  decided = false
  moved = false
  dragging.value = true
  // Захоплення вказівника не критичне — без нього свайп теж працює
  try {
    e.currentTarget.setPointerCapture?.(e.pointerId)
  } catch (err) {}
}

const onPointerMove = (e) => {
  if (!dragging.value) return

  const moveX = e.clientX - startX
  const moveY = e.clientY - startY

  // Поки не вирішили, що це горизонтальний жест, не перехоплюємо вертикальний
  // скрол сторінки
  if (!decided) {
    if (Math.abs(moveX) < DRAG_START && Math.abs(moveY) < DRAG_START) return
    if (Math.abs(moveX) < Math.abs(moveY)) {
      dragging.value = false
      return
    }
    decided = true
  }

  moved = true
  dx.value = moveX
}

const flyOut = (direction) => {
  flying.value = true
  dx.value = direction * 400
  setTimeout(() => emit('dismiss'), FLY_OUT_MS)
}

const onPointerUp = () => {
  if (!dragging.value) return
  dragging.value = false

  if (Math.abs(dx.value) >= SWIPE_THRESHOLD) flyOut(Math.sign(dx.value))
  else dx.value = 0
}

// Свайп закінчився там само, де почався клік, — відкривати чат не треба
const onClick = () => {
  if (moved || flying.value) return
  emit('open')
}
</script>

<template>
  <div
    class="group relative bg-base-200/90 shadow-lg py-2 pr-7 pl-3 rounded-lg max-w-full text-sm cursor-pointer touch-pan-y pointer-events-auto select-none"
    :style="{
      transform: dx ? `translateX(${dx}px)` : null,
      opacity: dx ? Math.max(0, 1 - Math.abs(dx) / 200) : null,
      transition: dragging ? null : `transform ${FLY_OUT_MS}ms ease, opacity ${FLY_OUT_MS}ms ease`
    }"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @click="onClick"
  >
    <!-- Прибрати, не відкриваючи чат -->
    <button
      class="top-1 right-1 absolute flex justify-center items-center opacity-40 hover:opacity-100 rounded w-5 h-5 transition-opacity"
      title="Приховати"
      @pointerdown.stop
      @click.stop="emit('dismiss')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
      >
        <line
          x1="18"
          y1="6"
          x2="6"
          y2="18"
        ></line>
        <line
          x1="6"
          y1="6"
          x2="18"
          y2="18"
        ></line>
      </svg>
    </button>

    <!-- З якого чату прийшло і, для аніме та серії, куди саме писали -->
    <div class="flex flex-wrap items-center gap-1 mb-0.5">
      <span
        class="px-1.5 py-px rounded font-medium text-[10px] uppercase"
        :class="
          bubble.source === 'twitch'
            ? 'bg-[#9147ff]/25 text-[#c9a4ff]'
            : 'bg-primary/25 text-primary'
        "
      >
        {{ SOURCE_LABELS[bubble.source] || bubble.source }}
      </span>

      <span
        v-if="bubble.target?.animeTitle"
        class="bg-base-content/10 px-1.5 py-px rounded max-w-[140px] text-[10px] text-base-content/60 truncate"
      >
        {{ bubble.target.animeTitle }}
      </span>

      <span
        v-if="bubble.target?.episodeTitle"
        class="bg-base-content/10 px-1.5 py-px rounded max-w-[110px] text-[10px] text-base-content/60 truncate"
      >
        {{ bubble.target.episodeTitle }}
      </span>
    </div>

    <div class="line-clamp-2">
      <span
        class="font-semibold"
        :style="bubble.color ? { color: bubble.color } : null"
        >{{ bubble.author }}:
      </span>
      <EmoteText :text="bubble.text" />
    </div>
  </div>
</template>
